import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, firstValueFrom } from 'rxjs';
import { WebLoginRequest, WebAuthResponse, WebUserInfo } from '../models/auth.model';
import { ApiResponse } from '../models/common.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'apex_auth_token';
  private readonly REFRESH_TOKEN_KEY = 'apex_refresh_token';
  private readonly USER_KEY = 'apex_user_info';

  currentUser = signal<WebUserInfo | null>(null);
  isAuthenticated = signal<boolean>(false);

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    this.loadUserFromStorage();
  }

  login(phoneNumber: string, password: string): Observable<WebAuthResponse> {
    const request: WebLoginRequest = { phoneNumber, password };

    return this.http.post<WebAuthResponse>(`${environment.apiUrl}/auth/web-login`, request)
      .pipe(
        tap(response => {
          if (response.success && response.data) {
            this.saveAuthData(response.data);
          }
        })
      );
  }

  private saveAuthData(data: any): void {
    localStorage.setItem(this.TOKEN_KEY, data.token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, data.refreshToken);
    localStorage.setItem(this.USER_KEY, JSON.stringify(data.user));

    this.currentUser.set(data.user);
    this.isAuthenticated.set(true);
  }

  private loadUserFromStorage(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const userJson = localStorage.getItem(this.USER_KEY);

    if (token && userJson) {
      try {
        const user = JSON.parse(userJson);
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
      } catch (error) {
        this.clearAuthData();
      }
    }
  }

  logout(): void {
    this.clearAuthData();
    this.router.navigate(['/login']);
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);

    this.currentUser.set(null);
    this.isAuthenticated.set(false);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  async loginWithToken(token: string): Promise<void> {
    localStorage.setItem(this.TOKEN_KEY, token);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<WebUserInfo>>(`${environment.apiUrl}/auth/me`)
      );

      if (response.success && response.data) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(response.data));
        this.currentUser.set(response.data);
        this.isAuthenticated.set(true);
        this.router.navigate(['/trainees']);
      } else {
        this.clearAuthData();
      }
    } catch {
      this.clearAuthData();
    }
  }
}
